import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const sections = [
  {
    title: 'Main Pages',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    links: [
      { name: 'Home', href: '/', desc: 'Ascali homepage — software solutions overview' },
      { name: 'About Us', href: '/about-us', desc: 'Our story, mission, values, and team' },
      { name: 'Products', href: '/products', desc: 'ERP, CRM, HRM, POS, and e-commerce solutions' },
      { name: 'Services', href: '/services', desc: 'All IT services offered by Ascali' },
      { name: 'Blogs', href: '/blogs', desc: 'Technology insights and industry guides' },
      { name: 'Contact Us', href: '/contact-us', desc: 'Get in touch with our Dubai team' }
    ]
  },
  {
    title: 'IT Services',
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-700',
    links: [
      { name: 'Web Development', href: '/services/web-development', desc: 'Custom websites and web applications' },
      { name: 'Mobile App Development', href: '/services/mobile-app-development', desc: 'iOS, Android, and cross-platform apps' },
      { name: 'Custom Software Development', href: '/services/custom-software-development', desc: 'Bespoke enterprise software solutions' },
      { name: 'Cloud Solutions', href: '/services/cloud-solutions', desc: 'Cloud migration and managed infrastructure' },
      { name: 'Data Analytics', href: '/services/data-analytics', desc: 'Business intelligence and data insights' },
      { name: 'UI/UX Design', href: '/services/ui-ux-design', desc: 'User-centred design for digital products' },
      { name: 'DevOps & AWS Services', href: '/services/devops-aws-services', desc: 'CI/CD, automation, and cloud operations' }
    ]
  },
  {
    title: 'Location Pages',
    color: 'from-green-500 to-teal-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    links: [
      { name: 'Software Development Company Dubai', href: '/software-development-company-dubai', desc: 'Dedicated landing page for Dubai, UAE market' }
    ]
  },
  {
    title: 'Future Service Pages',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    comingSoon: true,
    links: [
      { name: 'AI Development Services', href: '#', desc: 'Artificial intelligence and machine learning solutions — coming soon' },
      { name: 'ERP Implementation', href: '#', desc: 'End-to-end ERP consulting and deployment — coming soon' },
      { name: 'IT Consulting', href: '#', desc: 'Strategic technology advisory for UAE businesses — coming soon' },
      { name: 'Cybersecurity Services', href: '#', desc: 'Security audits, compliance, and managed security — coming soon' },
      { name: 'Digital Transformation', href: '#', desc: 'End-to-end digital transformation for enterprises — coming soon' }
    ]
  },
  {
    title: 'Future Location Pages',
    color: 'from-indigo-500 to-blue-500',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-700',
    comingSoon: true,
    links: [
      { name: 'Software Development Abu Dhabi', href: '#', desc: 'Abu Dhabi dedicated landing page — coming soon' },
      { name: 'Software Development Sharjah', href: '#', desc: 'Sharjah dedicated landing page — coming soon' },
      { name: 'Software Development UAE', href: '#', desc: 'UAE-wide landing page — coming soon' },
      { name: 'Software Company GCC', href: '#', desc: 'GCC region landing page — coming soon' }
    ]
  }
];

const SitemapPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <title>Sitemap | Ascali Software Development Company Dubai, UAE</title>
      <meta name="description" content="Browse the complete Ascali website sitemap. Find all pages including services, products, blogs, and location-specific pages for Dubai and UAE." />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10 text-center text-white">
          <span className="inline-block text-white/70 text-xs font-semibold uppercase tracking-widest mb-4 bg-white/10 px-4 py-1.5 rounded-full">
            Navigation
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="gradient-text">Sitemap</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            A complete index of all pages on the Ascali website — find exactly what you're looking for.
          </p>
        </div>
      </section>

      {/* Sitemap Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="space-y-10">
            {sections.map((section, si) => (
              <div key={si} className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                {/* Section Header */}
                <div className={`bg-gradient-to-r ${section.color} px-8 py-5 flex items-center gap-3`}>
                  <h2 className="font-display font-bold text-white text-xl">{section.title}</h2>
                  {section.comingSoon && (
                    <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Expanding
                    </span>
                  )}
                  <span className="ml-auto bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {section.links.length} {section.links.length === 1 ? 'page' : 'pages'}
                  </span>
                </div>

                {/* Links Grid */}
                <div className="p-6">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {section.links.map((link, li) => {
                      const isExternal = link.href === '#';
                      return (
                        <div
                          key={li}
                          className={`rounded-xl p-4 border ${section.borderColor} ${section.bgColor} ${!isExternal ? 'hover:shadow-md transition-all duration-200 hover:-translate-y-0.5' : 'opacity-70'}`}
                        >
                          {isExternal ? (
                            <div>
                              <div className={`flex items-center gap-2 font-semibold text-sm ${section.textColor} mb-1`}>
                                <svg className="w-3.5 h-3.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {link.name}
                              </div>
                              <p className="text-slate-500 text-xs leading-relaxed">{link.desc}</p>
                            </div>
                          ) : (
                            <Link to={link.href} className="block group">
                              <div className={`flex items-center gap-2 font-semibold text-sm ${section.textColor} mb-1 group-hover:underline`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                {link.name}
                              </div>
                              <p className="text-slate-500 text-xs leading-relaxed">{link.desc}</p>
                              <div className="mt-2 text-xs text-slate-400 font-mono">ascaliit.com{link.href}</div>
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* XML Sitemap note */}
          <div className="mt-10 bg-slate-900 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Looking for the XML Sitemap?</h3>
              <p className="text-slate-400 text-sm">
                The machine-readable XML sitemap for search engines is available at{' '}
                <span className="text-cyan-400 font-mono text-xs">https://ascaliit.com/sitemap.xml</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="container-custom text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            Our team is happy to help. Reach out and we'll point you in the right direction.
          </p>
          <Link
            to="/contact-us"
            className="inline-block bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-10 py-4 rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            Contact Us
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SitemapPage;
